/**
 * WebLoginScreen for Poker Reborn
 *
 * Web implementation: social login buttons are DOM elements overlaid on the PixiJS canvas.
 * Google button rendered by Google Identity Services (GIS) as icon-only.
 * Facebook, Apple are icon-only HTML buttons (scaffold).
 * No guest login on web.
 *
 * @see docs/screens/login/WEB.md - Web-specific specs
 * @see docs/screens/login/README.md - General login flow
 */

import { LoginScreenBase } from './LoginScreenBase';
import { authService } from '@/services/AuthService';
import { log } from '@/utils/log';

// =============================================================================
// GOOGLE IDENTITY SERVICES TYPES
// =============================================================================

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfig) => void;
          renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
          prompt: () => void;
          cancel: () => void;
        };
      };
    };
  }
}

interface GoogleIdConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonConfig {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  type?: 'standard' | 'icon';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
}

interface GoogleCredentialResponse {
  credential?: string;
  select_by?: string;
}

// =============================================================================
// BUTTON STYLES
// =============================================================================

const SOCIAL_ICON_STYLE = `
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: filter 0.15s ease;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

/** Social provider icon paths (PNG) */
const SOCIAL_ICONS: Record<string, string> = {
  facebook: 'assets/images/social/facebook.png',
  apple: 'assets/images/social/apple.png',
};

// =============================================================================
// WEB LOGIN SCREEN
// =============================================================================

export class WebLoginScreen extends LoginScreenBase {
  private buttonContainer: HTMLDivElement | null = null;
  private socialRow: HTMLDivElement | null = null;
  private googleButtonDiv: HTMLDivElement | null = null;
  private facebookButtonElement: HTMLButtonElement | null = null;
  private appleButtonElement: HTMLButtonElement | null = null;

  private googleScriptCheckInterval: ReturnType<typeof setInterval> | null = null;
  private googleScriptTimeout: ReturnType<typeof setTimeout> | null = null;

  // ===========================================================================
  // ABSTRACT IMPLEMENTATIONS
  // ===========================================================================

  protected mountButtons(): void {
    // Create wrapper div positioned over canvas center
    this.buttonContainer = document.createElement('div');
    this.buttonContainer.id = 'login-buttons-container';
    this.buttonContainer.style.cssText = `
      position: absolute;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
      z-index: 1000;
    `;
    document.body.appendChild(this.buttonContainer);

    // Social icon row — equal, no hierarchy
    this.socialRow = document.createElement('div');
    this.socialRow.style.cssText = 'display: flex; gap: 16px;';
    this.buttonContainer.appendChild(this.socialRow);

    // 1. Google button (GIS renders into this div, icon-only)
    this.googleButtonDiv = document.createElement('div');
    this.socialRow.appendChild(this.googleButtonDiv);
    this.initGoogleSignIn();

    // 2. Facebook button (icon-only — scaffold)
    this.facebookButtonElement = this.createSocialIconButton(
      'facebook',
      '#1877f2',
      '#1565d8',
      () => { void this.handleFacebookLogin(); }
    );
    this.socialRow.appendChild(this.facebookButtonElement);

    // 3. Apple button (icon-only — scaffold)
    this.appleButtonElement = this.createSocialIconButton(
      'apple',
      '#000000',
      '#222222',
      () => { void this.handleAppleLogin(); }
    );
    this.socialRow.appendChild(this.appleButtonElement);

    this.updateButtonPosition();
  }

  protected cleanupImpl(): void {
    // Clear GIS timers
    if (this.googleScriptCheckInterval) {
      clearInterval(this.googleScriptCheckInterval);
      this.googleScriptCheckInterval = null;
    }
    if (this.googleScriptTimeout) {
      clearTimeout(this.googleScriptTimeout);
      this.googleScriptTimeout = null;
    }

    // Cancel any pending GIS prompts
    try { window.google?.accounts?.id?.cancel(); } catch { /* ignore */ }

    // Remove entire wrapper div (removes all children too)
    if (this.buttonContainer) {
      this.buttonContainer.remove();
      this.buttonContainer = null;
    }

    // Fallback: ensure DOM container is removed even if reference was lost
    document.getElementById('login-buttons-container')?.remove();

    this.socialRow = null;
    this.googleButtonDiv = null;
    this.facebookButtonElement = null;
    this.appleButtonElement = null;
  }

  protected onResize(): void {
    this.updateButtonPosition();
  }

  // ===========================================================================
  // PRIVATE — DOM Button Helpers
  // ===========================================================================

  private createSocialIconButton(
    provider: string,
    backgroundColor: string,
    hoverColor: string,
    onClick: () => void
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.style.cssText = `${SOCIAL_ICON_STYLE} background-color: ${backgroundColor};`;

    // Add provider icon
    const iconPath = SOCIAL_ICONS[provider];
    if (iconPath) {
      const img = document.createElement('img');
      img.src = iconPath;
      img.alt = provider;
      img.style.cssText = 'width: 24px; height: 24px; pointer-events: none;';
      button.appendChild(img);
    }

    // Hover/press effects
    button.onmouseenter = () => { button.style.backgroundColor = hoverColor; };
    button.onmouseleave = () => { button.style.backgroundColor = backgroundColor; };
    button.onmousedown = () => { button.style.filter = 'brightness(0.85)'; };
    button.onmouseup = () => { button.style.filter = 'none'; };

    button.addEventListener('click', onClick);
    return button;
  }

  private updateButtonPosition(): void {
    if (!this.buttonContainer) return;

    const centerX = this.screenWidth / 2;
    const centerY = this.screenHeight / 2;

    this.buttonContainer.style.left = `${String(centerX)}px`;
    this.buttonContainer.style.top = `${String(centerY)}px`;
    this.buttonContainer.style.transform = 'translate(-50%, -50%)';
  }

  // ===========================================================================
  // PRIVATE — Google Identity Services
  // ===========================================================================

  private initGoogleSignIn(): void {
    // Check if GIS is already loaded
    if (window.google?.accounts?.id) {
      this.initializeGoogleButton();
    } else {
      // Poll for Google script to load
      this.googleScriptCheckInterval = setInterval(() => {
        if (window.google?.accounts?.id) {
          if (this.googleScriptCheckInterval) {
            clearInterval(this.googleScriptCheckInterval);
            this.googleScriptCheckInterval = null;
          }
          this.initializeGoogleButton();
        }
      }, 100);

      // Timeout after 10 seconds
      this.googleScriptTimeout = setTimeout(() => {
        if (this.googleScriptCheckInterval) {
          clearInterval(this.googleScriptCheckInterval);
          this.googleScriptCheckInterval = null;
        }
        if (!window.google?.accounts?.id) {
          log.app.error`Google Sign-In script failed to load`;
          this.showError('Failed to load Google Sign-In. Please refresh the page.');
        }
      }, 10000);
    }
  }

  private initializeGoogleButton(): void {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

    if (!clientId) {
      log.app.error`VITE_GOOGLE_CLIENT_ID not configured`;
      this.showError('Google Client ID not configured. Check environment variables.');
      return;
    }

    if (!this.googleButtonDiv) {
      log.app.error`Google button container not found`;
      return;
    }

    try {
      window.google!.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => { void this.handleGoogleResponse(response); },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google!.accounts.id.renderButton(this.googleButtonDiv, {
        theme: 'filled_black',
        size: 'large',
        type: 'icon',
        shape: 'circle',
      });

      log.app.info`Google Sign-In initialized (icon-only)`;
    } catch (error) {
      log.app.error`Failed to initialize Google Sign-In: ${error}`;
      this.showError('Failed to initialize Google Sign-In. Please refresh.');
    }
  }

  private async handleGoogleResponse(response: GoogleCredentialResponse): Promise<void> {
    if (!response.credential) {
      // User cancelled or closed popup — no error
      log.app.debug`Google sign-in cancelled by user`;
      return;
    }

    this.setStatus('Signing in...');
    this.hideError();

    try {
      const credentials = await authService.googleLoginWeb(response.credential);

      log.app.info`Google auth successful for ${credentials.username}`;

      // Cleanup and notify
      this.cleanup();
      this.onCredentialsReceived?.(credentials.username, credentials.password);
    } catch (error) {
      this.handleAuthError(error);
    }
  }

  // ===========================================================================
  // PRIVATE — Facebook & Apple (scaffold)
  // ===========================================================================

  private async handleFacebookLogin(): Promise<void> {
    this.setStatus('Signing in with Facebook...');
    this.hideError();

    if (this.facebookButtonElement) {
      this.facebookButtonElement.disabled = true;
    }

    try {
      const credentials = await authService.facebookLogin();

      this.cleanup();
      this.onCredentialsReceived?.(credentials.username, credentials.password);
    } catch (error) {
      if (this.facebookButtonElement) {
        this.facebookButtonElement.disabled = false;
      }

      const errorMessage = error instanceof Error ? error.message : 'Facebook sign-in failed';
      this.setStatus('');
      this.showError(errorMessage);
      log.auth.error`Facebook login failed: ${errorMessage}`;
    }
  }

  private async handleAppleLogin(): Promise<void> {
    this.setStatus('Signing in with Apple...');
    this.hideError();

    if (this.appleButtonElement) {
      this.appleButtonElement.disabled = true;
    }

    try {
      const credentials = await authService.appleLogin();

      this.cleanup();
      this.onCredentialsReceived?.(credentials.username, credentials.password);
    } catch (error) {
      if (this.appleButtonElement) {
        this.appleButtonElement.disabled = false;
      }

      const errorMessage = error instanceof Error ? error.message : 'Apple sign-in failed';
      this.setStatus('');
      this.showError(errorMessage);
      log.auth.error`Apple login failed: ${errorMessage}`;
    }
  }
}
